import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { getArticle, getRelated } from "../data/articles";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import SEO from "../components/SEO";

const HeroImage = ({ src, alt }) => (
  <div className="rounded-2xl overflow-hidden relative w-full" style={{ maxHeight: 400, backgroundColor: "var(--card-tint)" }}>
    <img
      src={src}
      alt={alt}
      loading="eager"
      className="w-full object-cover"
      style={{ maxHeight: 400, height: "min(50vh, 400px)" }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, transparent 55%, rgba(23,20,18,0.35) 100%)",
      }}
      aria-hidden
    />
  </div>
);

const RelatedCard = ({ a }) => (
  <Link
    to={`/blog/${a.slug}`}
    className="service-glow group flex flex-col h-full p-5 rounded-2xl border transition-all duration-500 hover:-translate-y-1"
    style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
    data-testid={`related-${a.slug}`}
  >
    <div className="aspect-[16/9] rounded-lg overflow-hidden mb-4 relative">
      <img
        src={a.heroImage}
        alt={a.heroAlt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
    </div>
    <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-subtle)" }}>
      <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {a.readTime}</span>
    </div>
    <h4 className="font-heading text-lg font-semibold mt-2 tracking-tight leading-tight" style={{ color: "var(--text)" }}>
      {a.title}
    </h4>
    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2DD4BF] group-hover:translate-x-1 transition-transform">
      Read More <ArrowRight size={14} />
    </span>
  </Link>
);

const BlogPost = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) return <Navigate to="/blog" replace />;
  const related = getRelated(slug);

  return (
    <div data-testid={`blog-post-${slug}`}>
      <SEO
        title={`${article.title} — Zelaflo Labs`}
        description={article.excerpt}
        path={`/blog/${slug}`}
      />

      <article className="relative pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm mb-8 hover:text-[#7B2FBE] transition-colors"
              style={{ color: "var(--text-muted)" }}
              data-testid="blog-back-link"
            >
              <ArrowLeft size={14} /> Back to Blog
            </Link>
          </Reveal>

          <Reveal delay={0.05}>
            <HeroImage src={article.heroImage} alt={article.heroAlt} />
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 flex items-center gap-4 text-sm" style={{ color: "var(--text-subtle)" }}>
              <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {article.date}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {article.readTime}</span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="prose-zl mt-8" data-testid="blog-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter mb-8 leading-[1.05]" style={{ color: "var(--text)" }}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight mt-12 mb-4 text-[#7B2FBE]">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight mt-8 mb-3" style={{ color: "var(--text)" }}>
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-base md:text-[17px] leading-[1.75] mb-5" style={{ color: "var(--text-muted)" }}>
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-6 space-y-2 mb-5 marker:text-[#7B2FBE]" style={{ color: "var(--text-muted)" }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-6 space-y-2 mb-5 marker:text-[#7B2FBE]" style={{ color: "var(--text-muted)" }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-base md:text-[17px] leading-[1.7]">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ color: "var(--text)", fontWeight: 600 }}>{children}</strong>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} className="text-[#2DD4BF] hover:text-[#7B2FBE] underline underline-offset-2 transition-colors">
                      {children}
                    </a>
                  ),
                  hr: () => <hr className="my-10 border-0 h-px" style={{ background: "var(--border)" }} />,
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>
          </Reveal>

          {/* Article CTA */}
          <Reveal delay={0.2}>
            <div
              className="mt-12 p-8 md:p-10 rounded-2xl relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, rgba(123,47,190,0.18) 0%, rgba(45,212,191,0.10) 100%)",
                border: "1px solid rgba(123,47,190,0.30)",
              }}
              data-testid="blog-post-cta"
            >
              <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: "var(--text)" }}>
                {article.cta.text}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={article.cta.primary.to}
                  className="btn-primary-zl inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                  data-testid="blog-cta-primary"
                >
                  {article.cta.primary.label} <ArrowRight size={14} />
                </Link>
                <Link
                  to={article.cta.secondary.to}
                  className="btn-outline-zl inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
                  data-testid="blog-cta-secondary"
                >
                  {article.cta.secondary.label}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <SectionDivider />

      {/* Related */}
      <section className="relative py-16 md:py-20" data-testid="related-posts-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Continue Reading</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tighter" style={{ color: "var(--text)" }}>
              Related posts
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {related.map((a) => (
              <Reveal key={a.slug}>
                <RelatedCard a={a} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
